import React, {useEffect, useMemo, useState} from 'react';
import groupDisplayName from '../../../utility/groupDisplayName';
import {useDispatch, useSelector} from 'react-redux';
import {useGetGroupFiltersQuery} from '../../../api/services/groupService';
import {useNavigate} from 'react-router-dom';
import {setFilterId, setFilterMembersType, setProfileFilters} from './ProfilesSlice';
import withPagination from '../../../hoc/pagination/withPagination';
import ProfilesGrid, {PROFILE_PAGE_SIZE} from './Components/ProfilesGrid';
import {useGetProfilesQuery} from '../../../api/services/profilesService';
import {
    Box,
    Chip,
    colors,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    MenuItem,
    Stack,
    styled,
    Switch,
    TextField,
    Button
} from '@mui/material';
import AdmicityDrawer from '../../../shared-components/AdmicityDrawer';
import {resetPaginationState} from '../../../hoc/pagination/paginationSlice';
import Typography from '@mui/material/Typography';
import useUser from '../../../utility/hooks/useUser';
import useNotification from '../../../utility/hooks/useNotification';
import SearchIcon from '@mui/icons-material/Search';
import useDebounce from '../../../utility/hooks/useDebounce';
import APPLICANT_STATUSES, {STAFF_STATUSES} from '../../../constants/applicantStatuses';
import BulkStatusUpdatePopup from './BulkStatusUpdatePopup/BulkStatusUpdatePopup';
import {GROUP_MEMBERS_TYPE} from '../../../constants/GroupMembersType';
import {ROLES} from '../../../constants/roles';
import PROFILE_TYPE from '../../../constants/profileTypes';

const GROUP_NAME_SEPARATOR = ' • ';
// Maria, 19 October 2026: the group above the cards with the top heading under its name on screen
const groupShownAs = value => String(value ?? '').split(GROUP_NAME_SEPARATOR)
    .map((part, index) => groupDisplayName(part, index === 0))
    .join(GROUP_NAME_SEPARATOR);
// Maria, 19 October 2026: the boxes, the search bar and the button above the cards are a little shorter
const TOP_ROW_HEIGHT = 38;

const SelectQuickFilter = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        backgroundColor: '#fff',
        height: TOP_ROW_HEIGHT,
        '& fieldset': {
            borderRadius: theme.spacing(1),
            borderColor: colors.blueGrey.A100,
        },
        '& input': {
            borderRadius: theme.spacing(1),
            backgroundColor: '#fff',
        },
    },
}));

const SwitchQuickFilter = styled(FormGroup)(({ theme }) => ({
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
    border: '1px solid',
    borderColor: colors.blueGrey.A100,
    paddingLeft: theme.spacing(0.5),
    height: TOP_ROW_HEIGHT,
    boxSizing: 'border-box',
    justifyContent: 'center',
    flexShrink: 0,
    '& .MuiFormControlLabel-root': {marginRight: theme.spacing(1.25)},
}));

const ProfilesOverview = () => {
    const {user} = useUser();
    const isParentRole = user.isParent();
    const filterId = useSelector(state => state.profilesInfo.filterId);
    const filterMembersType = useSelector(state => state.profilesInfo.filterMembersType);
    const profileFilters = useSelector(state => state.profilesInfo.profileFilters);
    // Maria, 19 October 2026: the first page shows as many profiles as fit on the screen, or a larger page in whole rows
    // (see cardsOnScreen), kept when the filters change
    const pageSize = useSelector(state => state.pagination.paginationModel.pageSize);
    const profilePageSize = pageSize || PROFILE_PAGE_SIZE;
    const {data: groupFilters = {modules: []}} = useGetGroupFiltersQuery(undefined, {
        skip: isParentRole
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const debouncedSearchStudentName = useDebounce(profileFilters.keyword.value, 500);

    const [bulkStatusUpdateOpen, setBulkStatusUpdateOpen] = useState(false);
    // AD-2228 (Maria, 22 October 2026): applicants by admissions category, nearest first within each; surname otherwise
    const [sortByCategoryAndDistance, setSortByCategoryAndDistance] = useState(false);
    const canSortByDistance = !isParentRole && filterMembersType !== GROUP_MEMBERS_TYPE.employee;
    // AD-2725 (Maria, 20 October 2026): staff are filtered by their role badge and account mark; a Status chosen on the
    // other list shows as All
    const statusOptions = filterMembersType === GROUP_MEMBERS_TYPE.employee ? STAFF_STATUSES : APPLICANT_STATUSES;
    const statusValue = statusOptions.some(option => option.value === profileFilters.status.value)
        ? profileFilters.status.value
        : 0;

    const fetchFunctionExtraProps = useMemo(() => {
        return {
            filterId,
            filterMembersType,
            isParent: isParentRole,
            status: profileFilters.status.value,
            searchTerms: {
                fullName: debouncedSearchStudentName
            },
            fetchOnlyMyChildren: profileFilters.fetchOnlyMyChildren.value,
            sortByAdmissionCategoryAndDistance: canSortByDistance && sortByCategoryAndDistance
        };
    }, [filterId, user.roles, profileFilters.status.value, debouncedSearchStudentName, profileFilters.fetchOnlyMyChildren, canSortByDistance, sortByCategoryAndDistance]);

    const fetchFunctionSettings = useMemo(() => ({
        skip: isParentRole ? !user.roles : !filterId || !user.roles
    }), [filterId, user.roles, isParentRole]);

    const Table = useMemo(() => {
        return withPagination(
            ProfilesGrid,
            useGetProfilesQuery,
            fetchFunctionExtraProps,
            fetchFunctionSettings
        );
    }, [fetchFunctionExtraProps, fetchFunctionSettings]);

    useEffect(() => {
        if (!filterId && groupFilters?.modules.length > 0) {
            dispatch(setFilterId(groupFilters.modules[0].id));
            dispatch(setFilterMembersType(GROUP_MEMBERS_TYPE.students));
            dispatch(setProfileFilters({
                group: groupFilters.modules[0].name
            }));
        }
    }, [groupFilters]);

    // Maria, 19 October 2026: a child a member of staff cannot open looks like every other card; they are told only
    // when they click on it
    const {showErrorNotification} = useNotification();
    const navigateToStudentProfile = (profile) => {
        if (profile?.isAccessRestricted) {
            const name = [profile.name, profile.surname].map(part => part?.trim()).filter(Boolean).join(' ');
            showErrorNotification(`You do not have access to ${name}'s student profile.`);
            return;
        }

        if (filterMembersType === GROUP_MEMBERS_TYPE.employee) {
            if (user.hasRole(ROLES.SCHOOL_ADMIN)) {
                navigate(`employees/${profile.id}`);
            }
        } else if (
            profile.personType === PROFILE_TYPE.APPLICATION ||
            profile.personType === PROFILE_TYPE.STUDENT ||
            profile.personType === PROFILE_TYPE.ENQUIRY
        ) {
            navigate(`students/${profile.id}`);
        }
    };

    return (
        <Box
            display="flex"
            flexGrow={1}
            minHeight="100%"
            // Maria, 19 October 2026: less space above the row of controls and the sidebar
            marginTop={0.75}
            sx={{
                flexDirection: {
                    xs: 'column',
                    sm: 'column',
                    md: 'column',
                    lg: 'row',
                    xl: 'row',
                }
            }}
        >
            {
                groupFilters?.modules.length !== 0
                    ? <AdmicityDrawer
                        filters={groupFilters?.modules.length !== 0
                            ? groupFilters.modules
                            : []
                        }
                        filterId={filterId}
                        onApply={(filterId, paths) => {
                            dispatch(setFilterId(filterId));
                            dispatch(setFilterMembersType(paths?.find(f => f.id === filterId)?.membersType));
                            dispatch(resetPaginationState({pageSize: profilePageSize}));
                            dispatch(setProfileFilters({
                                group: paths
                                    .filter((f, i) => i === 0 || f.selectable)
                                    .map(p => p.name)
                                    .join(GROUP_NAME_SEPARATOR),
                                keyword: ''
                            }));
                        }}
                        onFilterDeleted={(id) => {
                            if (filterId === id) {
                                dispatch(setProfileFilters({
                                    group: groupFilters.modules[0].name
                                }));
                                dispatch(setFilterId(groupFilters.modules[0].id));
                                dispatch(setFilterMembersType(GROUP_MEMBERS_TYPE.students));
                            }
                        }}
                        defaultSelected={filterId}
                        isParentRole={isParentRole}
                    />
                    : ''
            }
            <Box
                display="flex"
                flexDirection="column"
                width="100%"
                sx={{
                    flexGrow: 1,
                    minHeight: '100%',
                    // Maria, 19 October 2026: straight after the sidebar
                    paddingLeft: 0,
                    paddingRight: {
                        xs: 0,
                        sm: 0,
                        md: 0,
                        lg: 0,
                        xl: 0,
                    },
                }}
            >
                {
                    !isParentRole
                        ? <>
                            <Stack
                                flexDirection="row"
                                // Maria, 19 October 2026: one row on a wide screen, with as little space as possible
                                flexWrap={{xs: 'wrap', md: 'nowrap'}}
                                gap={1}
                                alignItems="end"
                                // 8px above the cards, the same as the space between them
                                marginBottom={1}
                            >
                                <TextField
                                    // Maria, 19 October 2026: in the same row as Group, Status and My children, just before Bulk
                                    // Status Update (order 1; the button is order 2)
                                    sx={{flex: '1 1 200px', minWidth: 200, order: 1}}
                                    // the box is narrower in the row, so the hint is shorter
                                    placeholder="Search by name"
                                    value={profileFilters.keyword.value}
                                    onChange={(e) => {
                                        dispatch(setProfileFilters({ keyword: e.target.value }));
                                        dispatch(resetPaginationState({pageSize: profilePageSize}));
                                    }}
                                    inputProps={{ maxLength: 200 }}
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small"/>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            height: TOP_ROW_HEIGHT,
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #339DFF',
                                            boxShadow: '0 0 4px rgba(51, 157, 255, 0.5)',
                                            '&.Mui-focused': {
                                                border: '1px solid #339DFF',
                                                outline: 'none',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#339DFF',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#339DFF',
                                            },
                                        }

                                    }}
                                />
                                {
                                    [profileFilters.group, profileFilters.keyword]
                                        .filter(f => f.value)
                                        .map(({label, value, deletable}) => (
                                            <Box
                                                key={label}
                                                display="inline-flex"
                                                alignItems="center"
                                                px={1}
                                                py={0.5}
                                                height={TOP_ROW_HEIGHT}
                                                // the group box gives up space first, as a long name already ends in '...'
                                                flexShrink={label === profileFilters.group.label ? 1 : 0}
                                                minWidth={label === profileFilters.group.label ? 120 : undefined}
                                                border="1px solid"
                                                borderColor={colors.blueGrey.A100}
                                                borderRadius={2}
                                                bgcolor="#fff"
                                                boxSizing="border-box"
                                                // Maria, 19 October 2026: a long group name ends in '...', with the whole name in the hover
                                                maxWidth={label === profileFilters.group.label ? 260 : '100%'}
                                                title={label === profileFilters.group.label ? groupShownAs(value) : undefined}
                                            >

                                    {/* Maria, 19 October 2026: the group's name on its own, without 'Group:' */}
                                    {label !== profileFilters.group.label && <Typography variant="subtitle2" fontWeight={600}>{label}:</Typography>}
                                                <Chip
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 2,
                                                        marginLeft: label === profileFilters.group.label ? 0 : 0.5,
                                                        height: '100%',
                                                        maxWidth: '100%',
                                                        // Maria, 19 October 2026: one outline round the group box, not a second one inside it
                                                        ...(label === profileFilters.group.label ? {border: 'none'} : {}),
                                                        '& .MuiChip-label': label === profileFilters.group.label
                                                            ? {padding: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}
                                                            : {padding: 0.75, overflowWrap: 'break-word', whiteSpace: 'normal', textOverflow: 'clip'}
                                                    }}
                                                    label={label === profileFilters.group.label ? groupShownAs(value) : value}
                                                    variant="outlined"
                                                    onDelete={deletable
                                                        ? () => dispatch(setProfileFilters({keyword: ''}))
                                                        : undefined
                                                    }
                                                />
                                            </Box>
                                        ))
                                }
                                {
                                    <>
                                        <SelectQuickFilter
                                            sx={{flexShrink: 0}}
                                            backgroundColor='white'
                                            size="small"
                                            select
                                            SelectProps={{
                                                renderValue: (value) =>
                                                    value != null
                                                        ? <span>
                                                <Typography
                                                    display="inline"
                                                    variant="subtitle2"
                                                    fontWeight={600}>
                                                    {profileFilters.status.label}: </Typography>
                                                <Typography
                                                    variant="subtitle2"
                                                    display="inline">
                                                    {statusOptions.find(x => x.value === value)?.label}
                                                </Typography>
                                        </span>
                                                        : profileFilters.status.label
                                            }}
                                            value={statusValue}
                                            onChange={e => {
                                                dispatch(setProfileFilters({
                                                    status: e.target.value
                                                }));
                                                dispatch(resetPaginationState({pageSize: profilePageSize}));
                                            }}>
                                            {
                                                statusOptions.map(option =>
                                                    <MenuItem key={option.label} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                )
                                            }
                                        </SelectQuickFilter>
                                        {canSortByDistance && <SelectQuickFilter
                                            sx={{flexShrink: 0}}
                                            backgroundColor='white'
                                            size="small"
                                            select
                                            inputProps={{'data-testid': 'profiles-sort'}}
                                            SelectProps={{
                                                renderValue: value => <span>
                                                    <Typography display="inline" variant="subtitle2" fontWeight={600}>Sort: </Typography>
                                                    <Typography variant="subtitle2" display="inline">
                                                        {/* the short name, so the row of boxes still fits; the list gives the full name */}
                                                        {value ? 'Category' : 'Surname'}
                                                    </Typography>
                                                </span>
                                            }}
                                            value={sortByCategoryAndDistance}
                                            onChange={e => {
                                                setSortByCategoryAndDistance(e.target.value);
                                                dispatch(resetPaginationState({pageSize: profilePageSize}));
                                            }}>
                                            <MenuItem value={false}>Surname</MenuItem>
                                            <MenuItem value={true}>Admissions Category and Distance</MenuItem>
                                        </SelectQuickFilter>}
                                        {filterMembersType !== GROUP_MEMBERS_TYPE.employee && <SwitchQuickFilter>
                                            <FormControlLabel
                                                control={<Switch
                                                    checked={profileFilters.fetchOnlyMyChildren.value}
                                                    onChange={e => {
                                                        dispatch(setProfileFilters({
                                                            fetchOnlyMyChildren: e.target.checked
                                                        }));
                                                        dispatch(resetPaginationState({pageSize: profilePageSize}));
                                                    }}
                                                />}
                                                label={profileFilters.fetchOnlyMyChildren.label}
                                                slotProps={{
                                                    typography: {
                                                        variant: 'subtitle2'
                                                    }
                                                }}/>
                                        </SwitchQuickFilter>}
                                        <Button
                                            data-testid="profiles-bulk-status-update"
                                            sx={{
                                                height: TOP_ROW_HEIGHT,
                                                order: 2,
                                                // Maria, 19 October 2026: no icon, smaller words
                                                px: 1,
                                                fontSize: 13,
                                                flexShrink: 0,
                                                whiteSpace: 'nowrap',
                                                color: '#186BBE',
                                                boxShadow: 'none',
                                                width: {xs: '100%', sm: 'auto'},
                                                borderColor: '#186BBE',
                                                // Maria, 19 October 2026: a white button, like the boxes beside it
                                                backgroundColor: '#fff',
                                                '&:hover': {backgroundColor: '#f5f9fd'}

                                            }}
                                            variant="outlined"
                                            onClick={() => setBulkStatusUpdateOpen(true)}
                                        >
                                            Bulk Status Update
                                        </Button>
                                    </>
                                }
                            </Stack>
                        </>
                        : ''
                }
                <Table
                    readonly={filterMembersType === GROUP_MEMBERS_TYPE.employee && !user.hasRole(ROLES.SCHOOL_ADMIN)}
                    onProfileSelect={navigateToStudentProfile}
                    noRowsOverlay={{
                        component: groupFilters?.modules.length > 0
                            ? <>
                                <Typography variant="h6">No Profiles Found</Typography>
                                {
                                    !isParentRole
                                        ? <Typography variant="subtitle1">No profiles match the selected filter
                                            criteria.</Typography>
                                        : ''
                                }
                            </>
                            : <>
                                <Typography variant="h6">No Data</Typography>
                                <Typography variant="subtitle1">Data is not synchronised yet.</Typography>
                            </>
                    }}
                />
                {
                    !isParentRole && bulkStatusUpdateOpen
                        ? <BulkStatusUpdatePopup
                            open={bulkStatusUpdateOpen}
                            groupFilters={groupFilters?.modules}
                            onClose={() => setBulkStatusUpdateOpen(false)}
                        />
                        : null
                }
            </Box>
        </Box>
    );
};

export default ProfilesOverview;